import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { usePrevious } from 'hooks';
import { Autocomplete } from 'components/FormFields';
import { handleShowError, handleError } from 'utils';
import { DocumentsActions } from 'state/modules/documents';
import { truncateKeywordSpaces } from '../../../utils/utils';

const DocumentTypesSelect = ({
  label,
  variant,
  field: { name, value },
  customparams,
  style,
  margin,
  id,
  form: { errors, touched, setFieldValue },
  disabled,
  asyncErrors,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customparams,
  });

  const { documentTypes, documentTypesLoading } = useSelector((state) => state.documents);
  const prevvalue = usePrevious(value);

  /**
   * Fetch vehicles list
   * @param {*} params
   */
  const fetchDocumentTypes = useCallback(
    debounce((params = {}) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(DocumentsActions.fetchDocumentTypes(newParams));
    }, 1000),
    []
  );

  const prev = usePrevious(customparams);

  useEffect(() => {
    if (prev && customparams && JSON.stringify(prev) !== JSON.stringify(customparams)) {
      fetchDocumentTypes({ ...params, ...customparams });
    }
  }, [customparams]) // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value || !defaultValue) {
      const defaultVal = documentTypes.find(
        (el) => el.id === parseInt(value) || el?.name === value || el?.id_slug === value
      );
      if (defaultVal && defaultVal?.id !== defaultValue?.id) {
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, documentTypes]) // eslint-disable-line

  useEffect(() => {
    if (!value) return;
    fetchDocumentTypes(params);
  }, []) // eslint-disable-line

  const handleFetchDocumentTypes = () => {
    if (documentTypes.length === 0 && !documentTypesLoading && !params?.keyword)
      dispatch(DocumentsActions.fetchDocumentTypes(params));
  };
  return (
    <Autocomplete
      id={id}
      name={name}
      dataCy={'documentTypesSelect'}
      style={style}
      label={label}
      margin={margin}
      onHighlightChange={handleFetchDocumentTypes}
      fullwidth="true"
      variant={variant}
      loading={documentTypesLoading}
      disabled={disabled}
      value={defaultValue}
      clearOnBlur={false}
      options={documentTypes || []}
      getOptionLabel={(option) => `${option?.name}`}
      error={handleError(name, errors, touched, asyncErrors)}
      getOptionSelected={(option, value) => option.id === value?.id}
      onChange={(ev, val) => setFieldValue(name, val?.id, val || '')}
      onInputChange={(ev, val) => fetchDocumentTypes({ ...params, keyword: val })}
      helperText={handleShowError(name, errors, touched, asyncErrors)}
    />
  );
};

DocumentTypesSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  margin: PropTypes.string,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  style: PropTypes.object,
  customparams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

DocumentTypesSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  style: {},
  margin: 'normal',
  customparams: {},
  variant: 'standard',
  label: 'Select...',
};

export default DocumentTypesSelect;
